// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — ARRÊT du lot de retenues d'une période, en deux temps et deux personnes, en SIMULATION :
//   1. demande (Trésorier, ds_retenues_arreter_demander) : lot ARRET_DEMANDE, verdicts recalculés ;
//   2. confirmation (Trésorier adjoint, ds_retenues_arreter_confirmer, jamais le demandeur) : lot ARRETE, empreinte ;
//      ou refus motivé : lot OUVERT.
// Mode vérification : l'arrêt ne transmet RIEN à la Paie et ne prélève rien (aucune route de transmission).
// Routes : POST /api/dispositifs/paie/periodes/:periode/arret/{demander,confirmer,refuser} (Idempotency-Key).
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { Lock, LockOpen, Send } from 'lucide-react';
import { Bouton, BandeauInfo } from '../../design/composants';
import { Etat } from './commun';
import { moisLong, dateHeureLongue, STATUTS_LOT } from './apiSocial';
import { FenetreActe, Infos, ChampMotif, motifValide } from './communSocial';
import { useMiseEnPlace } from './apiMiseEnPlace';

const RAPPEL = 'Mode vérification : l\'arrêt fige le lot pour contrôle ; rien n\'est transmis à la Paie ni prélevé sur un salaire.';

function FenetreArret({ organisationId, periode, etape, onFermer, onFait }) {
  const api = useMiseEnPlace();
  const [decision, setDecision] = useState('CONFIRMER');
  const [motif, setMotif] = useState('');
  if (etape === 'demander') {
    return (
      <FenetreActe titre={`Demander l'arrêt des retenues de ${moisLong(periode)}`} Icone={Lock} libelleBouton="Demander l'arrêt" onFermer={onFermer}
        aide="Les verdicts de l'arbitre sont recalculés. Une autre personne confirme ou refuse ensuite l'arrêt."
        onConfirmer={async (cle) => { await api.demanderArretLot(periode, organisationId, cle); onFait('Arrêt demandé : il attend la confirmation d\'une autre personne.'); }}>
        <BandeauInfo ton="alerte" titre="Aucune transmission à la Paie">{RAPPEL}</BandeauInfo>
      </FenetreActe>
    );
  }
  const refuser = decision === 'REFUSER';
  return (
    <FenetreActe titre={`Arrêt des retenues de ${moisLong(periode)}`} Icone={refuser ? LockOpen : Lock} libelleBouton={refuser ? 'Refuser et rouvrir le lot' : 'Confirmer l\'arrêt'}
      pret={!refuser || motifValide(motif)} danger={refuser} onFermer={onFermer}
      aide="La personne qui a demandé l'arrêt ne le confirme pas (double regard). La confirmation fige le contenu du lot par une empreinte."
      onConfirmer={async (cle) => {
        if (refuser) { await api.refuserArretLot(periode, organisationId, motif.trim(), cle); onFait('Arrêt refusé : le lot est rouvert.'); return; }
        const r = await api.confirmerArretLot(periode, organisationId, cle);
        onFait(`Lot arrêté${r?.empreinte ? ` (empreinte ${String(r.empreinte).slice(0, 16)}…)` : ''}. Rien n'est transmis à la Paie.`);
      }}>
      <BandeauInfo ton="alerte" titre="Aucune transmission à la Paie">{RAPPEL}</BandeauInfo>
      <div role="radiogroup" style={{ display: 'grid', gap: 6, marginBottom: 12 }}>
        {[['CONFIRMER', 'Confirmer l\'arrêt du lot'], ['REFUSER', 'Refuser l\'arrêt (le lot redevient ouvert)']].map(([c, l]) => (
          <label key={c} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
            <input type="radio" name="decision-arret" value={c} checked={decision === c} onChange={() => setDecision(c)} />{l}
          </label>
        ))}
      </div>
      {refuser && <ChampMotif valeur={motif} onChange={setMotif} />}
    </FenetreActe>
  );
}

/** Carte du lot de la période : état, dates, empreinte, actes d'arrêt selon mes pouvoirs. */
export default function ArretLot({ organisation, periode, lot, nombre, droits, onFait }) {
  const [fenetre, setFenetre] = useState(null);
  const statut = lot?.statut || 'OUVERT';
  const peutDemander = statut === 'OUVERT' && droits.a('ds_retenues_arreter_demander') && nombre > 0;
  const peutDecider = statut === 'ARRET_DEMANDE' && droits.a('ds_retenues_arreter_confirmer');
  return (
    <section className="rh-carte" data-lot-statut={statut}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <h2 className="rh-form-section-titre" style={{ margin: 0, display: 'flex', gap: 8, alignItems: 'center' }}>Lot de {moisLong(periode)} <Etat table={STATUTS_LOT} code={statut} /></h2>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {peutDemander && <Bouton petit Icone={Lock} onClick={() => setFenetre('demander')}>Demander l'arrêt</Bouton>}
          {peutDecider && <Bouton petit variante="primaire" Icone={Send} onClick={() => setFenetre('decider')}>Confirmer ou refuser l'arrêt</Bouton>}
        </div>
      </div>
      <Infos lignes={[
        ['Mode', 'Vérification (simulation)'],
        ['Arrêt demandé le', lot?.arret_demande_le ? dateHeureLongue(lot.arret_demande_le) : null],
        ['Arrêt confirmé le', lot?.arret_confirme_le ? dateHeureLongue(lot.arret_confirme_le) : null],
        ['Empreinte', lot?.empreinte ? <span title={lot.empreinte}>{String(lot.empreinte).slice(0, 16)}…</span> : null],
      ]} />
      <p className="rh-form-aide" style={{ margin: '10px 0 0' }}>
        {statut === 'ARRET_DEMANDE' && !droits.a('ds_retenues_arreter_confirmer') ? 'Arrêt demandé : la confirmation revient à une autre personne (Trésorier adjoint). ' : ''}
        {!lot && !nombre ? 'Aucun lot : il naît avec la première retenue proposée de la période. ' : ''}
        Arrêter le lot ne transmet rien à la Paie : la transmission reste fermée tant que le fondement juridique et les validations requises manquent.
      </p>
      {fenetre && <FenetreArret organisationId={organisation.id} periode={periode} etape={fenetre} onFermer={() => setFenetre(null)} onFait={(m) => { setFenetre(null); onFait(m); }} />}
    </section>
  );
}
